import React, { createContext, useContext, useState, useEffect } from 'react';

type PaletteMode = 'light' | 'dark';

interface ThemeContextType {
  mode: PaletteMode;
  toggleTheme: () => void;
  setMode: (mode: PaletteMode) => void;
}

const ThemeContext = createContext<ThemeContextType>({
  mode: 'light',
  toggleTheme: () => {},
  setMode: () => {},
});

export const useThemeMode = () => useContext(ThemeContext);

interface ThemeProviderProps {
  children: React.ReactNode;
}

/**
 * Apply saved custom theme CSS variables from localStorage.
 * The theme editor saves the FULL resolved theme (defaults + preset + overrides)
 * under the `resolved` key, so we apply all variables — not just overrides.
 */
function applyCustomTheme(isDark: boolean) {
  try {
    const raw = localStorage.getItem('titan-theme-custom');
    if (!raw) return;
    const data = JSON.parse(raw);

    // Prefer the fully resolved theme (written by ThemeEditor.handleSave)
    const resolved = isDark ? data.resolvedDark : data.resolvedLight;
    const vars = resolved || (isDark ? data.dark : data.light);
    if (!vars || typeof vars !== 'object') return;

    const root = document.documentElement;
    for (const [key, value] of Object.entries(vars)) {
      if (typeof value === 'string') {
        root.style.setProperty(`--${key}`, value);
      }
    }
  } catch {
    // Ignore parse errors
  }
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<PaletteMode>(() => {
    const saved = localStorage.getItem('titan-theme-mode');
    if (saved === 'dark' || saved === 'light') return saved;
    if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  });

  useEffect(() => {
    localStorage.setItem('titan-theme-mode', mode);

    // Apply Tailwind dark mode class to HTML root
    if (mode === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    // Apply any saved custom theme
    applyCustomTheme(mode === 'dark');

    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', mode === 'dark' ? '#111318' : '#EDE8E0');
    }
  }, [mode]);

  const toggleTheme = () => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme, setMode }}>
      {children}
    </ThemeContext.Provider>
  );
};
